import type { ProviderResult } from '@/lib/pricing'
import { fmtUSD } from '@/lib/pricing'
import {Sparkles, TrendingDown} from 'lucide-react'

interface Props {
  results: ProviderResult[]
}

function RecommendationCard({ results }: Props) {
  const cheapest = results[0]
  const priciest = results[results.length - 1]
  const monthlySavings = priciest.total - cheapest.total
  const annualSavings = monthlySavings * 12
  const savingsPct = ((priciest.total - cheapest.total) / priciest.total) * 100

  return (
    <div className="relative rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-[#0E1525] via-[#0E1525] to-emerald-950/30 overflow-hidden">
      <div className="absolute inset-0 starfield opacity-40 pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none"></div>

      <div className="relative p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Recommendation</span>
        </div>

        <div className="flex items-baseline gap-3 mb-1">
          <span className="text-3xl font-bold gradient-text tracking-tight">
            {cheapest.provider.shortName}
          </span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">cheapest</span>
        </div>
        <p className="text-[12px] text-slate-400 mb-5">
          {cheapest.provider.name} delivers the lowest total cost for this workload profile.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-lg border border-[#1F2A44] bg-[#0B0F1C] p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Monthly cost</div>
            <div className="font-mono text-xl font-bold text-emerald-300 tabular-nums">{fmtUSD(cheapest.total)}</div>
          </div>
          <div className="rounded-lg border border-[#1F2A44] bg-[#0B0F1C] p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">vs. {priciest.provider.shortName}</div>
            <div className="font-mono text-xl font-bold text-cyan-300 tabular-nums">−{savingsPct.toFixed(1)}%</div>
          </div>
        </div>

        <div className="flex items-center gap-3 px-3.5 py-3 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20">
          <TrendingDown className="w-4 h-4 text-emerald-400 shrink-0" />
          <div className="flex-1">
            <div className="text-[12px] text-slate-300">
              Save <span className="font-mono font-bold text-emerald-300">{fmtUSD(monthlySavings)}</span> /mo
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-0.5">
              ≈ {fmtUSD(annualSavings)} annualized
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecommendationCard
